import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { useTheme, alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import ButtonBase from "@mui/material/ButtonBase";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import ScheduleIcon from "@mui/icons-material/Schedule";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import api from "/imports/api/client";
import useFetch from "/imports/hooks/useFetch";
import { checkInOpensLabel, useCheckInPhase } from "/imports/hooks/useCheckInOpen";
import { shortDateLabel } from "/imports/ui/components/recommendations/recommendationsHelpers";
import { CheckInDialog, InstallAppCard } from "/imports/ui/components/shared";
import { currentCardState, onPushChange } from "/imports/libs/push";

/**
 * Home hero — the one bold, personal element on the front door. Signed-in
 * users get a greeting on a brand band; when they have an upcoming booking it
 * also carries a "next up" strip that deep-links to the dashboard bookings.
 * Anonymous visitors get the club's identity (logo + name + tagline) with
 * sign-in CTAs, so the page opens with who this is rather than bare cards.
 */
function HomeHero({ go }) {
  const theme = useTheme();
  const { slug, widget, saas, push } = useWidget();
  const { isAuthenticated, activeClient, client } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [reloadKey, setReloadKey] = useState(0);
  const { data, loading: dashboardLoading } = useFetch(() => api.getUserDashboard(slug), [slug, activeClient?._id, reloadKey], {
    skip: !slug || !isAuthenticated
  });

  // useCheckInOpen opens the button on a timer, but browsers throttle or defer
  // timers in hidden tabs — refetch on return so a phone that was locked
  // through the moment the window opened catches up.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") setReloadKey((n) => n + 1);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  const [checkInDialog, setCheckInDialog] = useState({ open: false, booking: null, submitting: false });
  const [checkedInOverride, setCheckedInOverride] = useState(null);

  // Derived above the anonymous early return so every hook below stays
  // unconditional.
  const next = data?.upcomingBookings?.[0] || null;
  // Opens the button on a timer when the window arrives, so a front door left
  // open while the client drives in doesn't need a reload to catch up.
  const checkInPhase = useCheckInPhase(next?.checkInWindow);
  const checkInOpen = checkInPhase === "open";

  // Whether the install strip has anything to show (phones only). Followed through
  // the strip's own changes, so the row closes up when it is put away. The band is
  // the one place the app is offered.
  const pushEnabled = !!push?.enabled && !!push?.publicKey;
  const [installCardState, setInstallCardState] = useState("hidden");
  useEffect(() => {
    if (!slug) return undefined;
    const read = () => setInstallCardState(currentCardState(slug, { pushEnabled }));
    read();
    return onPushChange(read);
  }, [slug, pushEnabled]);

  const confirmCheckIn = async () => {
    const booking = checkInDialog.booking;
    if (!booking) return;
    setCheckInDialog((s) => ({ ...s, submitting: true }));
    try {
      const result = await api.checkInBooking(slug, booking._id);
      setCheckedInOverride({ bookingId: booking._id, checkIn: result.checkIn });
      setCheckInDialog({ open: false, booking: null, submitting: false });
      enqueueSnackbar(result.alreadyCheckedIn ? "You were already checked in." : "You're checked in.", { variant: "success" });
    } catch (err) {
      setCheckInDialog((s) => ({ ...s, submitting: false }));
      enqueueSnackbar(err.message || "Failed to check in", { variant: "error" });
    }
  };

  // Derived from the club's primary color so custom-branded widgets keep
  // their own hue (palette.light/dark are computed from primary).
  const bandSx = {
    mb: { xs: 2, md: 3 },
    p: { xs: 2, md: 3 },
    borderRadius: 4,
    color: "common.white",
    background: `linear-gradient(120deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${theme.palette.primary.light} 100%)`,
    boxShadow: `0 10px 30px ${alpha(theme.palette.primary.main, 0.25)}`
  };

  if (!isAuthenticated) {
    return (
      <Box sx={bandSx}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
          {widget?.logoUrl && (
            <Avatar
              src={widget.logoUrl}
              alt={saas?.name || widget?.name || "Club"}
              variant="rounded"
              sx={{ width: { xs: 48, md: 56 }, height: { xs: 48, md: 56 }, bgcolor: "common.white", p: 0.5, flexShrink: 0 }}
            />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: "1.3rem", md: "1.6rem" }, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.2 }}>
              {saas?.name || widget?.name ? `Welcome to ${saas?.name || widget?.name}` : "Welcome"}
            </Typography>
            <Typography sx={{ mt: 0.25, fontSize: { xs: "0.85rem", md: "0.95rem" }, color: "rgba(255,255,255,0.85)" }}>
              Book courts, lessons, classes and more — right here.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            onClick={() => go("login")}
            sx={{ bgcolor: "common.white", color: "primary.dark", fontWeight: 700, "&:hover": { bgcolor: "rgba(255,255,255,0.88)" } }}
          >
            Sign in
          </Button>
          <Button
            variant="outlined"
            onClick={() => go("signup")}
            sx={{
              color: "common.white",
              borderColor: "rgba(255,255,255,0.6)",
              fontWeight: 600,
              "&:hover": { borderColor: "common.white", bgcolor: "rgba(255,255,255,0.1)" }
            }}
          >
            Create account
          </Button>
        </Box>
      </Box>
    );
  }

  const person = activeClient || client;
  const firstName = person?.firstName || "";
  const nextMeta = next ? [next.trainer && `with ${next.trainer}`, next.location].filter(Boolean).join(" — ") : "";

  // useFetch has no refetch, so a successful check-in is held locally and wins
  // over the fetched row until the next natural reload.
  const checkedIn = next && (checkedInOverride?.bookingId === next._id ? checkedInOverride.checkIn : next.checkIn);
  const showCheckIn = checkInOpen && !checkedIn;

  // Safe to nest inside the strip, unlike the Check in button: a status has no
  // click for the surrounding ButtonBase to swallow.
  const checkedInPill = checkedIn ? (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 0.875,
        py: 0.25,
        borderRadius: 999,
        bgcolor: "rgba(255,255,255,0.22)"
      }}
    >
      <HowToRegIcon sx={{ fontSize: "0.875rem" }} />
      <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, lineHeight: 1.3, whiteSpace: "nowrap" }}>
        Checked in{checkedIn.label ? ` ${checkedIn.label}` : ""}
      </Typography>
    </Box>
  ) : null;

  // Until the window opens there is no button, and nothing on the front door
  // hints that checking in is a thing this club does — so the strip says when
  // it will appear instead of leaving the client to discover it by chance.
  // Quieter than the checked-in pill: this is information, not a done deed.
  const checkInOpensAt = !checkedIn && checkInPhase === "before" ? checkInOpensLabel(next.checkInWindow.opensAt, saas?.timeZone) : null;
  const checkInOpensPill = checkInOpensAt ? (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 0.875,
        py: 0.25,
        borderRadius: 999,
        bgcolor: "rgba(255,255,255,0.16)",
        border: "1px solid rgba(255,255,255,0.28)"
      }}
    >
      <ScheduleIcon sx={{ fontSize: "0.875rem" }} />
      <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, lineHeight: 1.3, whiteSpace: "nowrap" }}>Check-in opens {checkInOpensAt}</Typography>
    </Box>
  ) : null;

  // One slot, never two: a booking is either already checked in or still
  // waiting for its window to open.
  const statusPill = checkedInPill || checkInOpensPill;

  // The install strip shares the booking strip's row, stacking under it on a phone.
  // It waits for the dashboard fetch so the two land together instead of the strip
  // showing alone and then moving when the booking arrives.
  const showInstallCard = !dashboardLoading && installCardState !== "hidden";

  return (
    <Box sx={bandSx}>
      <Typography sx={{ fontSize: { xs: "1.3rem", md: "1.6rem" }, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.2 }}>
        {firstName ? `Welcome back, ${firstName}` : "Welcome back"}
      </Typography>
      <Typography sx={{ mt: 0.25, fontSize: { xs: "0.85rem", md: "0.95rem" }, color: "rgba(255,255,255,0.85)" }}>
        {next ? "Here's what's coming up." : "Ready for your next session?"}
      </Typography>

      {(next || showInstallCard) && (
        <Box sx={{ mt: 1.75, display: "flex", alignItems: "stretch", flexWrap: "wrap", gap: 1 }}>
          {next && (
            <>
              <ButtonBase
                // The card is about the next session, which may be days out — the
                // Bookings tab defaults to Today and would open empty underneath it.
                onClick={() => go("dashboard?tab=bookings&scope=upcoming")}
                sx={{
                  width: { xs: "100%", md: "auto" },
                  // Hugging its content left most of the band empty on a wide screen.
                  // Capped rather than full-width: stretched edge to edge, the chevron
                  // ends up stranded a long way from the text it belongs to.
                  flex: { md: "1 1 auto" },
                  maxWidth: { md: "34rem" },
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  px: 2,
                  py: 1.25,
                  borderRadius: 3,
                  textAlign: "left",
                  bgcolor: "rgba(255,255,255,0.16)",
                  border: "1px solid rgba(255,255,255,0.28)",
                  backdropFilter: "blur(4px)",
                  transition: "background-color 0.15s ease",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.24)" }
                }}
              >
                <EventAvailableIcon sx={{ fontSize: "1.375rem", flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, lineHeight: 1.3 }} noWrap>
                    {next.serviceName} · {shortDateLabel(next.date)} at {next.time}
                  </Typography>
                  {nextMeta && (
                    <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.85)", lineHeight: 1.3 }} noWrap>
                      {nextMeta}
                    </Typography>
                  )}
                  {/* Desktop has room for it beside the text; a phone doesn't, and
                  the title is already truncating there. */}
                  {statusPill && <Box sx={{ display: { xs: "block", md: "none" }, mt: 0.5 }}>{statusPill}</Box>}
                </Box>
                {statusPill && <Box sx={{ display: { xs: "none", md: "flex" }, flexShrink: 0 }}>{statusPill}</Box>}
                <ChevronRightIcon sx={{ fontSize: "1.25rem", flexShrink: 0 }} />
              </ButtonBase>

              {/* Sibling of the strip, never nested inside it — the strip is a
              ButtonBase, and a button within a button swallows this click. */}
              {showCheckIn && (
                <Button
                  variant="contained"
                  onClick={() => setCheckInDialog({ open: true, booking: next, submitting: false })}
                  startIcon={<HowToRegIcon />}
                  sx={{
                    width: { xs: "100%", md: "auto" },
                    // md keeps auto so it still matches the strip's height beside it.
                    height: { xs: "2rem", md: "auto" },
                    fontSize: "0.85rem",
                    // MUI's own `.MuiButton-startIcon > *` rule outranks sx on the icon.
                    "& .MuiButton-startIcon > *:nth-of-type(1)": { fontSize: "1.125rem" },
                    px: 2.5,
                    borderRadius: 3,
                    bgcolor: "common.white",
                    color: "primary.dark",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.88)", boxShadow: "none" }
                  }}
                >
                  Check in
                </Button>
              )}
            </>
          )}

          {showInstallCard && (
            <InstallAppCard
              variant="hero"
              // Capped like the strip: on a tablet it sits beside the strip rather than filling the band.
              sx={{ width: { xs: "100%", md: "auto" }, flex: { md: "1 1 20rem" }, maxWidth: { md: "36rem" }, minWidth: 0 }}
            />
          )}
        </Box>
      )}

      <CheckInDialog
        state={checkInDialog}
        onClose={() => {
          if (!checkInDialog.submitting) setCheckInDialog({ open: false, booking: null, submitting: false });
        }}
        onConfirm={confirmCheckIn}
      />
    </Box>
  );
}

export default HomeHero;
